import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

const CancelOrderModal = ({ order, transition, isOpen, onClose, onSubmit, loading }) => {
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!notes.trim()) {
            setError('Please provide a reason for cancellation.');
            return;
        }
        onSubmit(order, transition, { notes: notes.trim() });
    };

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100 flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-5 flex items-center justify-between border-b border-rose-100 bg-rose-50/50 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-black tracking-tight text-rose-800">
                            Cancel Order
                        </h2>
                        <p className="text-xs text-gray-500 font-medium mt-0.5">
                            Order {order.orderRef || `#${order.publicId?.slice(-8)}`} • {order.customer?.name}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-all">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div className="flex gap-3 bg-rose-50 border border-rose-100 rounded-2xl p-4 text-rose-700 text-sm font-semibold">
                        <AlertCircle className="text-rose-500 flex-shrink-0" size={20} />
                        <div>
                            <p className="font-bold">Are you sure you want to cancel this order?</p>
                            <p className="text-xs text-rose-600 font-medium mt-0.5">This action will cancel the order in the system. This cannot be undone.</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 px-1">
                            Reason for Cancellation <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => {
                                setNotes(e.target.value);
                                if (error) setError('');
                            }}
                            placeholder="Please explain why this order is being cancelled..."
                            className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border transition-all text-sm min-h-[100px] focus:ring-4 outline-none ${
                                error ? 'border-rose-300 focus:ring-rose-100' : 'border-gray-200 focus:ring-rose-100 focus:border-rose-400'
                            }`}
                        />
                        {error && (
                            <div className="flex items-center gap-1.5 mt-2 ml-1 text-rose-600">
                                <AlertCircle size={14} />
                                <span className="text-xs font-bold">{error}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 flex-shrink-0">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 py-3.5 rounded-2xl text-sm font-black text-gray-500 bg-white border border-gray-200 hover:bg-gray-100 transition-all uppercase tracking-widest"
                    >
                        Keep Order
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="flex-1 py-3.5 rounded-2xl text-sm font-black text-white bg-rose-500 hover:bg-rose-600 shadow-xl shadow-rose-200 transition-all uppercase tracking-widest"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                            </div>
                        ) : (
                            'Confirm Cancel'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CancelOrderModal;
